/**
 * Utility functions for date categorization
 */

/**
 * Determines if a date falls on a weekend (Saturday or Sunday)
 */
export function isWeekendDay(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // Domenica (0) o Sabato (6)
}

/**
 * Determines if a date is tomorrow
 */
export function isTomorrow(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date.getTime() === tomorrow.getTime();
}

/**
 * Determines if an event has at least one day that falls in the NEXT weekend (Friday, Saturday, or Sunday).
 * The "next weekend" is defined as:
 * - If today is before Friday of current week: the upcoming Friday-Saturday-Sunday of this week
 * - If today is Friday/Saturday/Sunday: the current weekend
 * - If today is after Sunday: the upcoming Friday-Saturday-Sunday of next week
 * 
 * This is the unified weekend detection logic used throughout the codebase.
 */
export function hasWeekendDay(startDateStr: string, endDateStr?: string): boolean {
  if (!startDateStr) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const todayDayOfWeek = today.getDay(); // 0=Dom, 1=Lun, ..., 5=Ven, 6=Sab
  
  // Calcola il prossimo weekend (ven-sab-dom)
  let nextFriday: Date;
  let nextSunday: Date;
  
  if (todayDayOfWeek === 0) {
    // Se oggi è domenica, il prossimo weekend è quello della prossima settimana
    nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + 5); // Venerdì prossimo
    nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + 7); // Domenica prossima
  } else if (todayDayOfWeek <= 4) {
    // Se oggi è lunedì-giovedì, il prossimo weekend è quello di questa settimana
    const daysUntilFriday = 5 - todayDayOfWeek;
    nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + daysUntilFriday);
    nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilFriday + 2);
  } else {
    // Se oggi è venerdì o sabato, il prossimo weekend è quello corrente
    if (todayDayOfWeek === 5) {
      // Venerdì: weekend è oggi-domani-dopodomani
      nextFriday = new Date(today);
      nextSunday = new Date(today);
      nextSunday.setDate(today.getDate() + 2);
    } else {
      // Sabato: weekend è ieri-oggi-domani
      nextFriday = new Date(today);
      nextFriday.setDate(today.getDate() - 1);
      nextSunday = new Date(today);
      nextSunday.setDate(today.getDate() + 1);
    }
  }
  
  nextFriday.setHours(0, 0, 0, 0);
  nextSunday.setHours(23, 59, 59, 999);

  const startDate = new Date(startDateStr);
  const endDate = endDateStr ? new Date(endDateStr) : startDate;

  // Normalizza le date dell'evento
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  // Verifica se l'evento si sovrappone al prossimo weekend
  // L'evento si sovrappone se: startDate <= nextSunday AND endDate >= nextFriday
  return startDate <= nextSunday && endDate >= nextFriday;
}

/**
 * @deprecated Use hasWeekendDay instead. This function is kept for backward compatibility.
 * Determines if a date falls on this weekend (Saturday or Sunday of current week)
 */
export function isThisWeekend(dateStr: string): boolean {
  // Per compatibilità, verifica se la data singola è ven/sab/dom
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();
  return dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0;
}

/**
 * Determines if a date falls on this week (Monday-Friday of current week)
 */
export function isThisWeek(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();

  // Calcola l'inizio della settimana corrente (lunedì)
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay() + 1);
  startOfWeek.setHours(0, 0, 0, 0);

  // Calcola la fine della settimana corrente (domenica)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  // Verifica se la data è nei giorni feriali (lunedì-venerdì) della settimana corrente
  const day = date.getDay();
  const isWeekday = day >= 1 && day <= 5; // Lunedì (1) a Venerdì (5)
  const isThisWeek = date >= startOfWeek && date <= endOfWeek;

  return isWeekday && isThisWeek;
}

/**
 * Determines if a date is within the next N days
 */
export function isWithinNextDays(dateStr: string, days: number): boolean {
  const date = new Date(dateStr);
  const today = new Date();
  const future = new Date(today);
  future.setDate(today.getDate() + days);
  future.setHours(23, 59, 59, 999);

  return date >= today && date <= future;
}

/**
 * Determines if an event is active today
 */
export function isActiveToday(startDateStr: string, endDateStr?: string): boolean {
  const startDate = new Date(startDateStr);
  const endDate = endDateStr ? new Date(endDateStr) : startDate;
  const today = new Date();

  return startDate <= today && endDate >= today;
}

/**
 * @deprecated Use hasWeekendDay instead. This function is kept for backward compatibility.
 * Determines if an event is active during this weekend.
 */
export function isActiveThisWeekend(startDateStr: string, endDateStr?: string): boolean {
  return hasWeekendDay(startDateStr, endDateStr);
}

/**
 * Determines if an event is concluded (in the past)
 */
export function isConcluded(startDate?: string, endDate?: string): boolean {
  if (!startDate) return false;
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Fine della giornata

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  if (!endDate) {
    // Evento di un solo giorno: concluso se la data di inizio è nel passato
    return start.getTime() < today.getTime();
  }

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  // Evento con durata: concluso se la data di fine è nel passato
  return end.getTime() < today.getTime();
}